import React, { createContext, useCallback, useEffect, useReducer, useRef } from 'react';
import { audioReducer, initialState } from './audioReducer';
import { setSessionMetadata, setSessionState, bindActions } from './mediaSession';
import type { AudioPlayerContextValue, PlaybackRate, RepeatMode, Track } from './types';

export const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null);

export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(audioReducer, initialState);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const acquireWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch { /* ignore */ }
  }, []);

  const releaseWakeLock = useCallback(() => {
    try { wakeLockRef.current?.release?.(); } catch { /* ignore */ }
    wakeLockRef.current = null;
  }, []);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const p = audio.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err: DOMException) => {
        if (err?.name === 'NotAllowedError') {
          dispatch({ type: 'SET_ERROR', payload: 'Tap to start playback.' });
        } else {
          dispatch({ type: 'SET_ERROR', payload: err?.message ?? 'Playback error.' });
        }
      });
    }
    dispatch({ type: 'PLAY' });
    void acquireWakeLock();
  }, [acquireWakeLock]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    dispatch({ type: 'PAUSE' });
    releaseWakeLock();
  }, [releaseWakeLock]);

  const toggle = useCallback(() => {
    if (stateRef.current.isPlaying) pause();
    else play();
  }, [play, pause]);

  const seek = useCallback((timeMs: number) => {
    if (audioRef.current) audioRef.current.currentTime = timeMs / 1000;
    dispatch({ type: 'SEEK', payload: timeMs });
  }, []);

  const setPlaybackRate = useCallback((rate: PlaybackRate) => {
    const a = audioRef.current as (HTMLAudioElement & {
      preservesPitch?: boolean;
      mozPreservesPitch?: boolean;
      webkitPreservesPitch?: boolean;
    }) | null;
    if (a) {
      a.playbackRate = rate;
      if ('preservesPitch' in a) a.preservesPitch = true;
      if ('mozPreservesPitch' in a) a.mozPreservesPitch = true;
      if ('webkitPreservesPitch' in a) a.webkitPreservesPitch = true;
    }
    dispatch({ type: 'SET_PLAYBACK_RATE', payload: rate });
  }, []);

  const setRepeatMode = useCallback((mode: RepeatMode) => {
    dispatch({ type: 'SET_REPEAT_MODE', payload: mode });
  }, []);

  const next = useCallback(() => dispatch({ type: 'NEXT_TRACK' }), []);
  const previous = useCallback(() => dispatch({ type: 'PREVIOUS_TRACK' }), []);

  const skipForward = useCallback((byMs: number) => {
    const a = audioRef.current;
    if (!a) return;
    seek(Math.min((a.currentTime + byMs / 1000), a.duration || Infinity) * 1000);
  }, [seek]);
  const skipBackward = useCallback((byMs: number) => {
    const a = audioRef.current;
    if (!a) return;
    seek(Math.max(a.currentTime - byMs / 1000, 0) * 1000);
  }, [seek]);

  const enqueue = useCallback((track: Track, opts?: { autoplay?: boolean }) => {
    dispatch({ type: 'SET_QUEUE', payload: { tracks: [track], startIndex: 0 } });
    if (opts?.autoplay) {
      // play after audio src settles
      setTimeout(() => play(), 0);
    }
  }, [play]);

  const setQueue = useCallback((tracks: Track[], startIndex: number = 0, opts?: { autoplay?: boolean }) => {
    dispatch({ type: 'SET_QUEUE', payload: { tracks, startIndex } });
    if (opts?.autoplay) setTimeout(() => play(), 0);
  }, [play]);

  const clearQueue = useCallback(() => {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.removeAttribute('src');
    dispatch({ type: 'CLEAR_QUEUE' });
    releaseWakeLock();
  }, [releaseWakeLock]);

  const setAbMarkers = useCallback((start?: number, end?: number) => {
    dispatch({ type: 'SET_AB_MARKERS', payload: [start, end] });
  }, []);

  /* Sync src + metadata when track changes */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (state.currentTrack && state.currentTrack.audibleUrl) {
      if (audio.src !== state.currentTrack.audibleUrl) {
        audio.src = state.currentTrack.audibleUrl;
        audio.load();
      }
      setSessionMetadata(state.currentTrack);
    }
  }, [state.currentTrack]);

  /* Sync playback state (for media session UI) */
  useEffect(() => {
    setSessionState(state.isPlaying);
  }, [state.isPlaying]);

  /* Wire audio element events */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => {
      dispatch({ type: 'UPDATE_DURATION', payload: Math.round((audio.duration || 0) * 1000) });
      dispatch({ type: 'SET_STATUS', payload: stateRef.current.isPlaying ? 'playing' : 'paused' });
    };
    const onTimeUpdate = () => {
      const tMs = Math.round(audio.currentTime * 1000);
      dispatch({ type: 'UPDATE_TIME', payload: tMs });

      const s = stateRef.current;
      if (s.repeatMode === 'pallavi' && s.currentTrack?.pallaviEndTimeMs && tMs >= s.currentTrack.pallaviEndTimeMs) {
        audio.currentTime = 0;
      }
      if (s.currentTrack?.lyricLineTimings && s.currentTrack.lyricLineTimings.length > 0) {
        const timings = s.currentTrack.lyricLineTimings;
        let lineIdx = -1;
        for (let i = 0; i < timings.length; i++) {
          if (tMs >= timings[i]) lineIdx = i;
          else break;
        }
        const next = lineIdx >= 0 ? lineIdx : undefined;
        if (next !== s.activeLyricLineIndex) {
          dispatch({ type: 'SET_ACTIVE_LYRIC_LINE', payload: next });
        }
      }
    };
    const onEnded = () => {
      const s = stateRef.current;
      if (s.repeatMode === 'one') {
        audio.currentTime = 0;
        void audio.play();
        return;
      }
      if (s.queue.length > 0 && s.queueIndex < s.queue.length - 1) {
        dispatch({ type: 'NEXT_TRACK' });
        setTimeout(() => audioRef.current?.play().catch(() => {}), 0);
      } else {
        dispatch({ type: 'PAUSE' });
      }
    };
    const onError = () => {
      const code = audio.error?.code;
      const msg = code === 4 ? 'Network error loading audio.' : code === 3 ? 'Audio file unsupported.' : 'Audio error.';
      dispatch({ type: 'SET_ERROR', payload: msg });
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, []);

  /* Bind Media Session actions once */
  useEffect(() => {
    bindActions({
      play: () => play(),
      pause: () => pause(),
      previoustrack: () => previous(),
      nexttrack: () => next(),
      seekforward: () => skipForward(10_000),
      seekbackward: () => skipBackward(10_000),
      seekto: (details?: MediaSessionActionDetails) => { if (typeof details?.seekTime === 'number') seek(details.seekTime * 1000); },
    });
  }, [play, pause, previous, next, skipForward, skipBackward, seek]);

  /* Reacquire wake lock on visibility */
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && stateRef.current.isPlaying) {
        void acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [acquireWakeLock]);

  /* Release on unmount */
  useEffect(() => () => releaseWakeLock(), [releaseWakeLock]);

  const value: AudioPlayerContextValue = {
    state, play, pause, toggle, seek, setPlaybackRate, setRepeatMode,
    next, previous, skipForward, skipBackward, enqueue, setQueue, clearQueue, setAbMarkers,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
      <audio ref={audioRef} crossOrigin="anonymous" preload="metadata" />
    </AudioPlayerContext.Provider>
  );
};
